import { ComponentProps } from "react";
import Card from "@particles/card/Card";
import { getMetaDetails } from "@particles/card/utils/helpers";
import { CardVariations } from "@particles/card/utils/types";
import { WEALTH_CARD_VARIANTS } from "@routes/Section/layouts/Wealth/utils/constant";
import { SanitizedFeedSectionArticle } from "@typings/Cue";

/**
 * Helper function to get the article props for the wealth top stories component
 * @param articles - The articles to get the props for
 * @returns {ComponentProps<typeof Card>[]} The article props for the wealth top stories component
 */
export const getWealthTopStoriesProps = (
  articles: SanitizedFeedSectionArticle[]
): ComponentProps<typeof Card>[] => {
  const cardProps = articles
    .slice(0, 9)
    .map(({ id, kicker, title, sections, urlPath, media, paywall }) => {
      const mainSection = sections?.[0];
      const _meta = getMetaDetails(mainSection);
      const mainMedia = media[0];
      const imgSrc = mainMedia?.landscape?.url;
      const imgWidth = mainMedia?.landscape?.width;
      const imgHeight = mainMedia?.landscape?.height;
      const props: ComponentProps<typeof Card> = {
        id,
        kicker: kicker?.fields?.[0]?.value?.toUpperCase(),
        title,
        meta: _meta,
        displayPaywall: paywall?.contentAccess === "1",
        link: urlPath,
        defaultImage: { directoryName: mainSection?.uniqueName || "" },
        thumbnail:
          imgSrc && imgWidth && imgHeight
            ? {
                src: imgSrc,
                alt: mainMedia.alt || mainMedia.caption || "",
                width: imgWidth,
                height: imgHeight,
              }
            : undefined,
      };

      return props;
    });

  return JSON.parse(JSON.stringify(cardProps)); // JSON.stringify to remove properties with undefined values
};

/**
 * Helper function to get the variant for the wealth top stories component
 * @param index - The index of the article
 * @returns {CardVariations} The variant for the wealth top stories component
 */
export const getWealthTopStoriesVariant = (index: number): CardVariations => {
  switch (index) {
    case 0:
      return WEALTH_CARD_VARIANTS.topStory;

    default:
      return WEALTH_CARD_VARIANTS.subStory;
  }
};
